import {
  ArchiveBoxIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowPathIcon,
  BellAlertIcon,
  CubeIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  QrCodeIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
  SignalIcon,
  Squares2X2Icon,
  TagIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { type Role, useAuth } from "../contexts/AuthContext";
import { useBandwidthAlertCount } from "../hooks/useBandwidthAlertCount";
import { useChatUnread } from "../hooks/useChatUnread";
import { cn } from "../lib/utils";

import AppBar from "./AppBar";
import Drawer from "./Drawer";
import SanitizedBanner from "./SanitizedBanner";

interface NavItem {
  name: string;
  href: string;
  /** Optional. Top-level items have icons; child items are icon-less
   *  by default (some children opt in — e.g. RFID Tags / SmartBoxes
   *  children). Admin subpages under Settings render without an icon. */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  matchPrefix?: string;
  /** Roles allowed to see this nav item. Omit = all roles. The operator
   *  triage views are platform-only; customers/vendors get the limited
   *  map + (forthcoming) inventory. */
  roles?: Role[];
  /** Capability required to see this nav item (RBAC). Omit = no cap gate. */
  cap?: string;
  /** Any-of capability gate. The nav entry shows when the user has at
   *  least ONE of the listed capabilities. Use when a page surfaces
   *  multiple tabs and the user might have only one. */
  anyCap?: string[];
  /** Sub-items rendered under a chevron when this entry is expanded.
   *  Visibility of each child is filtered the same way as top-level
   *  items (roles + cap + anyCap). A parent is hidden entirely when
   *  ALL its children get filtered out AND it has no standalone
   *  purpose of its own — but most parents are also clickable
   *  destinations (e.g. SmartBoxes itself navigates to /boxes), so
   *  the parent stays even when all children are filtered. */
  children?: NavItem[];
}

const NAV: NavItem[] = [
  // Dashboard — landing route for every authenticated role. Donut + KPIs
  // scoped server-side to the user's visible smartboxes, with the fleet
  // map embedded inline (2026-06-03 — Jason). /map still routes here.
  { name: "Dashboard", href: "/", icon: Squares2X2Icon },
  // Customer-facing destinations — visible to every role.
  { name: "Inventory", href: "/inventory", icon: ArchiveBoxIcon, matchPrefix: "/inventory" },
  // Parts — catalog browser. Read-only for all roles; visibility
  // scoped server-side (vendors → own; customers → vendors of their
  // smartboxes; platform → all).
  { name: "Parts List", href: "/parts", icon: CubeIcon, matchPrefix: "/parts" },
  { name: "Refill", href: "/refill", icon: ClipboardDocumentCheckIcon, matchPrefix: "/refill" },
  { name: "Reports", href: "/reports", icon: DocumentChartBarIcon, matchPrefix: "/reports" },
  // SOPs — standard operating procedures from rebound-platform-docs. Platform
  // users only (backend 403s the rest); content synced daily.
  { name: "SOPs", href: "/sops", icon: DocumentTextIcon, matchPrefix: "/sops", roles: ["platform"] },
  // Messages — DMs. Customers/vendors auto-route to Jason; platform users
  // can DM each other. Unread badge populated by useChatUnread.
  { name: "Messages", href: "/messages", icon: ChatBubbleLeftRightIcon, matchPrefix: "/messages" },
  // RFID Tags — vendors + platform stock the boxes; customers don't.
  // The parent route is the lifecycle / Tag Status page (was the
  // "Tag Status" entry); Match + Manage nest as the sub-tools that
  // edit the underlying associations.
  {
    name: "RFID Tags", href: "/tags/status", icon: TagIcon, matchPrefix: "/tags/status", cap: "tags.manage",
    children: [
      { name: "Match Tags", href: "/tags/match", icon: QrCodeIcon, matchPrefix: "/tags/match", cap: "tags.manage" },
      { name: "Manage Tags", href: "/tags/manage", icon: TagIcon, matchPrefix: "/tags/manage", cap: "tags.manage" },
    ],
  },
  // SmartBoxes — primary entry; the activity destinations (Manual
  // Tickets, Departures, Arrivals) nest underneath since they're all
  // "things that happened at a smartbox" (2026-06-18, Jason). The
  // operator-triage trio (Maintenance, Uptime, Recovery) also nests
  // here — they're all per-smartbox health views.
  {
    name: "SmartBoxes", href: "/boxes", icon: RectangleStackIcon, matchPrefix: "/boxes",
    children: [
      // Manual checkout / checkin — MTTR-native MC/MI tickets. One page
      // with two tabs; gated by either of the two capabilities (the page
      // itself surfaces only the tab the user can use).
      // Vendors are blocked from the manual-checkout flow (mirrors
      // gen5-pwa's companyType=2 short-circuit — vendors don't pull
      // parts from customer boxes, they track via Departures). The
      // page itself also refuses vendor accounts; this hides the nav.
      { name: "Manual Tickets", href: "/manual", icon: ClipboardDocumentCheckIcon, matchPrefix: "/manual", roles: ["platform", "customer"], anyCap: ["inventory.manual_checkout", "inventory.manual_checkin"] },
      // Departures — camera person-episode + scan-miss reconciler ("who took
      // what"). All roles; server scopes by SCU access + (for vendors) by item.
      { name: "Departures", href: "/departures", icon: ArrowRightOnRectangleIcon, matchPrefix: "/departures" },
      // Arrivals — v2_tag_event ARRIVAL stream ("what showed up"). Mirror
      // of Departures; same scoping rules.
      { name: "Arrivals", href: "/arrivals", icon: ArrowLeftOnRectangleIcon, matchPrefix: "/arrivals" },
      // Scan a box now and list the tags that just arrived (migration 177).
      // Vendors + platform (reader.vendor_scan).
      { name: "New Scan", href: "/scan/new", icon: ArrowPathIcon, matchPrefix: "/scan", cap: "reader.vendor_scan" },
      // Operator triage — platform only (operator.triage capability).
      { name: "Maintenance", href: "/outages", icon: ChartBarIcon, matchPrefix: "/outages", cap: "operator.triage" },
      { name: "Uptime", href: "/uptime", icon: ChartPieIcon, matchPrefix: "/uptime", cap: "operator.triage" },
      { name: "Recovery", href: "/recovery", icon: ClockIcon, matchPrefix: "/recovery", cap: "operator.triage" },
      // Bandwidth alerts — one entry per unacknowledged Peplink alert.
      // Nav-badge count populated by useBandwidthAlertCount.
      { name: "Bandwidth", href: "/bandwidth", icon: SignalIcon, matchPrefix: "/bandwidth", cap: "operator.triage" },
    ],
  },
  // Alerts — platform-only operator page, gated on the same
  // dashboard.tag_health cap as the Dashboard tag-health tile. Each tab is
  // also a nav child (Jason 2026-09-16): an alert nobody knows to look for is
  // an alert nobody acts on, and /alerts lands on whichever tab is first, so
  // the second one would otherwise be invisible from the sidebar. The parent
  // link still works — it redirects to TABS[0].
  {
    name: "Alerts", href: "/alerts", icon: BellAlertIcon, matchPrefix: "/alerts",
    cap: "dashboard.tag_health",
    children: [
      // Tags that read badly (FLICKERY / UNRELIABLE).
      {
        name: "Unhealthy Tags", href: "/alerts/unhealthy-tags",
        icon: ExclamationTriangleIcon, matchPrefix: "/alerts/unhealthy-tags",
        cap: "dashboard.tag_health",
      },
      // Tags that read fine but belong to no product.
      {
        name: "Unmatched Tags", href: "/alerts/unmatched-tags",
        icon: QuestionMarkCircleIcon, matchPrefix: "/alerts/unmatched-tags",
        cap: "dashboard.tag_health",
      },
    ],
  },
  // Settings — a single nav button (Jason 2026-08-25). The admin functions
  // are TABS on the consolidated /settings page, not nav-dropdown children, so
  // the sidebar stays uncluttered; each tab is capability-gated inside
  // SettingsPage (and every /settings/<tab> deep link still works directly).
  { name: "Settings", href: "/settings", icon: Cog6ToothIcon, matchPrefix: "/settings" },
  // "Signal" is a per-box detail page (`/signal/:scuName`) — reached by
  // clicking a row on Boxes/Outages/Map. Not a top-level destination.
];

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user, role, can, logout, isLoggingOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const unreadChats = useChatUnread();
  const bandwidthAlerts = useBandwidthAlertCount();

  // Nav filter: role gate (operator items are platform-only) AND capability
  // gate (Settings needs users.manage). Recurses into children so a
  // parent's submenu only shows the items the user can actually reach.
  const itemVisible = (item: NavItem): boolean =>
    Boolean(
      (!item.roles || (role && item.roles.includes(role))) &&
      (!item.cap || can(item.cap)) &&
      (!item.anyCap || item.anyCap.some((c) => can(c))),
    );

  const nav: NavItem[] = NAV.filter(itemVisible).map((item) => ({
    ...item,
    children: item.children?.filter(itemVisible),
  }));

  // Close the drawer whenever the route changes — common UX pattern so
  // tapping a nav link doesn't leave a half-open drawer behind.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isCurrent = (item: NavItem): boolean => {
    if (item.matchPrefix) return location.pathname.startsWith(item.matchPrefix);
    return location.pathname === item.href;
  };

  // Submenu expansion state. A parent is "expanded" when its chevron is
  // open. We auto-expand any parent whose own route or one of whose
  // children matches the current pathname so users don't have to
  // re-open the submenu after navigating into it. Manual toggles
  // persist via sessionStorage so paging around doesn't reset state.
  const SUBMENU_STORAGE_KEY = "mttr.nav.submenuExpanded";
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(SUBMENU_STORAGE_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  // Auto-expand whichever parent contains the current route.
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const item of nav) {
        if (!item.children?.length) continue;
        const containsCurrent =
          isCurrent(item) || item.children.some((c) => isCurrent(c));
        if (containsCurrent) next.add(item.href);
      }
      return next;
    });
  }, [location.pathname]);
  // Persist on change.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        SUBMENU_STORAGE_KEY, JSON.stringify(Array.from(expanded)),
      );
    } catch {
      /* sessionStorage unavailable; ignore */
    }
  }, [expanded]);

  const toggleExpanded = (href: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });
  };

  // Shared nav body — rendered both inside the desktop sidebar and the
  // mobile drawer. DRY ensures the two stay visually identical.
  const navBody = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-primary-600 shrink-0">
        <Link to="/" className="inline-block bg-white rounded-md px-2 py-1.5">
          <img
            src={`${process.env.PUBLIC_URL || ""}/Rebound_Logo.png`}
            alt="Rebound Dynamics"
            className="h-8 w-auto"
          />
        </Link>
        <div className="text-xs text-primary-100 mt-1">Control Center</div>
      </div>
      <ul className="flex-1 p-2 space-y-1 overflow-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          const current = isCurrent(item);
          const hasChildren = !!item.children?.length;
          const isOpen = expanded.has(item.href);
          // A parent counts as "current" when itself OR any child
          // route matches — keeps the row highlighted while you're
          // poking around in the submenu.
          const anyChildCurrent =
            hasChildren && item.children!.some((c) => isCurrent(c));
          return (
            <li key={item.href}>
              <div className="flex items-stretch">
                <Link
                  to={item.href}
                  className={cn(
                    // min-h-[44px] hits Apple HIG / Material touch-target floor.
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors min-h-[44px]",
                    hasChildren ? "flex-1 rounded-r-none" : "flex-1",
                    current || anyChildCurrent
                      ? "bg-primary-600 text-white"
                      : "text-primary-100 hover:bg-primary-400",
                  )}
                >
                  {Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                  <span className="flex-1">{item.name}</span>
                  {item.href === "/messages" && unreadChats > 0 && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white text-primary-700"
                      aria-label={`${unreadChats} unread messages`}
                    >
                      {unreadChats}
                    </span>
                  )}
                  {item.href === "/bandwidth" && bandwidthAlerts > 0 && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-error-600 text-white"
                      aria-label={`${bandwidthAlerts} open bandwidth alerts`}
                    >
                      {bandwidthAlerts}
                    </span>
                  )}
                </Link>
                {hasChildren && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.href)}
                    className={cn(
                      "px-2 rounded-md rounded-l-none text-primary-100 hover:bg-primary-400 transition-colors",
                      (current || anyChildCurrent) && "bg-primary-600 text-white hover:bg-primary-600/80",
                    )}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? "Collapse" : "Expand"} ${item.name} submenu`}
                  >
                    {isOpen
                      ? <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                      : <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />}
                  </button>
                )}
              </div>
              {hasChildren && isOpen && (
                <ul className="mt-1 ml-3 pl-3 border-l border-primary-400/40 space-y-0.5">
                  {item.children!.map((child) => {
                    const ChildIcon = child.icon;
                    const childCurrent = isCurrent(child);
                    return (
                      <li key={child.href}>
                        <Link
                          to={child.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors min-h-[36px]",
                            childCurrent
                              ? "bg-primary-600 text-white"
                              : "text-primary-100 hover:bg-primary-400",
                          )}
                        >
                          {ChildIcon && <ChildIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                          <span className="flex-1">{child.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className="p-4 border-t border-primary-600 text-xs text-primary-100 shrink-0">
        <div className="flex items-center gap-1">
          <HomeIcon className="h-4 w-4 inline" />
          <span className="truncate">{user?.display_name ?? "—"}</span>
        </div>
        {user && (
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="px-1.5 py-0.5 rounded bg-primary-600 text-primary-50 uppercase tracking-wide text-[10px]">
              {user.role}
            </span>
            <button
              type="button"
              onClick={() => {
                void logout();
              }}
              disabled={isLoggingOut}
              className="text-primary-100 hover:text-white underline underline-offset-2 disabled:opacity-50"
            >
              {isLoggingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full flex">
      {/* Desktop sidebar — always visible at lg:+. */}
      <nav className="hidden lg:flex w-56 bg-primary-500 text-white">
        {navBody}
      </nav>

      {/* Mobile drawer — slides in over content. Closes on backdrop /
          Escape / route change. */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {navBody}
      </Drawer>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Sticky top app bar on mobile only. Hidden at lg:+. */}
        <AppBar onOpenDrawer={() => setDrawerOpen(true)} />
        {/* Renders nothing on an ordinary session. On a sanitised one it is
            the only thing explaining why prices are blank and parts are named
            PART-418823 -- without it that reads as broken data. */}
        <SanitizedBanner />
        {/* min-h-0 lets this flex child actually CONSTRAIN to its share of the
            viewport and scroll internally, instead of min-height:auto letting
            it grow to content. Without it a tall page (e.g. the Fleet Map's
            Active Issues list) stretches `main`, and any h-full child (the map)
            balloons to that height. */}
        <main className="flex-1 overflow-auto min-h-0">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
